import { FC, useCallback, useState } from "react";
import { Combobox, ComboboxOption, Label } from "@altimateai/lego";
import { useQuery } from "@tanstack/react-query";
import { getUserGroups } from "@/helpers/apis";
import { useDebounce } from "@/helpers/hooks";

interface ShareTeamComboboxProps {
  selectedTeamIds: string[];
  onChange: (teamIds: string[]) => void;
  /** Label above the picker. Defaults to "Select teams". */
  label?: string;
}

/**
 * Multi-select team picker backed by the lego Combobox. Owns the team
 * search query so every share surface (link tab, email tab) reuses the
 * same data flow instead of each duplicating it.
 */
const ShareTeamCombobox: FC<ShareTeamComboboxProps> = ({
  selectedTeamIds,
  onChange,
  label = "Select teams",
}) => {
  const [search, setSearch] = useState("");
  // The Combobox reports every keystroke; debounce so we don't fire a
  // team-search request per character typed.
  const debouncedSearch = useDebounce(search, 300);

  const { data } = useQuery({
    queryKey: ["teams-search-share", debouncedSearch],
    queryFn: () => getUserGroups({ search: debouncedSearch, size: 20 }),
    staleTime: 30000,
  });

  const options: ComboboxOption[] = (data?.items ?? []).map(team => ({
    value: team.id,
    label: team.name,
    labelNode: (
      <div>
        <p className="al-text-sm al-font-medium">{team.name}</p>
        {team.description && (
          <p className="al-text-xs al-text-muted-foreground">{team.description}</p>
        )}
      </div>
    ),
  }));

  // The Combobox drives search through onLoadMore; updating local state
  // re-runs the query above with the new term.
  const handleLoadMore = useCallback(async (searchValue?: string) => {
    setSearch(searchValue ?? "");
  }, []);

  return (
    <div className="al-space-y-3">
      <Label>{label}</Label>
      <Combobox
        multiSelect
        options={options}
        value={selectedTeamIds}
        onChange={onChange}
        placeholder="Select teams..."
        searchPlaceholder="Search teams..."
        onLoadMore={handleLoadMore}
        hasMore={false}
        buttonProps={{ className: "al-w-full", "aria-label": "Select teams" }}
      />
    </div>
  );
};

export default ShareTeamCombobox;
